import React, { useMemo } from 'react';
import { SparklesIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';

import type { AICatalog } from '../hooks/useAICommands';
import { buildSuggestedPrompts } from './chatHelpers';
import { useAorText } from 'utils/aorTerminology';

export {
  buildSuggestedPrompts,
  FIXED_STARTERS,
  type SuggestedPrompt,
} from './chatHelpers';

export interface EmptyStateProps {
  catalog?: AICatalog;
  hasIncident: boolean;
  orgName?: string;
  onPickPrompt: (prompt: string) => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  catalog,
  hasIncident,
  orgName,
  onPickPrompt,
}) => {
  const aorText = useAorText();
  const prompts = useMemo(() => buildSuggestedPrompts(catalog), [catalog]);

  return (
    <Empty
      className="tw:my-auto tw:border-0 tw:p-6"
      data-testid="ai-empty-state"
    >
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <SparklesIcon />
        </EmptyMedia>
        <EmptyTitle className="tw:text-base">
          {hasIncident ? 'Ask about this incident' : 'Ask PRATUS AI'}
        </EmptyTitle>
        <EmptyDescription>
          {hasIncident
            ? 'Summaries, gap analyses, ICS forms, and edits you approve before they land.'
            : `Working at the ${orgName ? `${orgName} ` : ''}organization level: search data sources, review automation rules, or draft a new one.`}
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <ul
          className="tw:m-0 tw:flex tw:w-full tw:list-none tw:flex-col tw:gap-1.5 tw:p-0"
          aria-label="Suggested prompts"
        >
          {prompts.map((p) => (
            <li key={p.prompt}>
              <Button
                variant="outline"
                size="sm"
                className="tw:h-auto tw:w-full tw:justify-start tw:whitespace-normal tw:py-2 tw:text-left"
                onClick={() => onPickPrompt(aorText(p.prompt))}
              >
                {aorText(p.label)}
              </Button>
            </li>
          ))}
        </ul>
        <p className="tw:m-0 tw:mt-2 tw:text-xs tw:text-muted-foreground">
          PRATUS AI generated content may be incorrect. Always check important
          information.
        </p>
      </EmptyContent>
    </Empty>
  );
};

export default EmptyState;
